import { useEffect } from 'react';
import { AppProviders } from './providers';
import { AppRouter } from './router';
import { httpClient } from '@/lib/api/client';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/lib/config/routes';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { STORAGE_KEYS } from '@/lib/config/constants';
import { authRepository } from '@/features/auth/repository';

function RealtimeSyncInit() {
  useRealtimeSync();
  return null;
}

export function App() {
  const clearSession = useAuthStore((s) => s.clearSession);

  useEffect(() => {
    httpClient.setUnauthorizedHandler(() => {
      clearSession();
      if (window.location.pathname !== ROUTES.login) {
        window.location.replace(ROUTES.login);
      }
    });
  }, [clearSession]);

  useEffect(() => {
    const sessionUserId = (raw: string | null): string | null => {
      try {
        return raw ? (JSON.parse(raw)?.state?.user?.id ?? null) : null;
      } catch {
        return null;
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEYS.authUser) return;
      const current = useAuthStore.getState().user?.id ?? null;
      if (sessionUserId(e.newValue) !== current) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!useAuthStore.getState().isAuthenticated) return;
    authRepository
      .session()
      .then((s) => useAuthStore.getState().syncSession(s))
      .catch(() => {});
  }, []);

  return (
    <AppProviders>
      <RealtimeSyncInit />
      <AppRouter />
    </AppProviders>
  );
}
